import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, ShieldOff, KeyRound, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

// Lightweight QR via Google Chart API (no extra dep). The provisioning_uri is
// the canonical otpauth:// string — Google Authenticator, Authy, 1Password and
// Microsoft Authenticator all accept it.
function QrFromUri({ uri }) {
  if (!uri) return null;
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(uri)}`;
  return (
    <img
      src={src}
      alt="2FA QR code"
      className="w-[220px] h-[220px] bg-white p-2"
      data-testid="totp-qr-image"
    />
  );
}

export default function AdminSecurity() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null); // {secret, provisioning_uri}
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [totpEnabled, setTotpEnabled] = useState(false);

  const refreshMe = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setTotpEnabled(!!data?.totp_enabled);
    } catch (err) {
      // /auth/me fails silently when the session cookie hasn't loaded yet;
      // surface in dev console only so we don't generate noise in production
      // observability for an expected transient state.
      if (process.env.NODE_ENV === "development") console.warn("refreshMe failed", err);
    }
  };
  useEffect(() => { setTotpEnabled(!!user?.totp_enabled); refreshMe(); }, [user]);

  const startSetup = async () => {
    setErr(""); setMsg(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
      setSetupData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to start setup");
    } finally { setLoading(false); }
  };

  const enableTotp = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, { code }, { withCredentials: true });
      setMsg("Two-Factor Authentication enabled. You will be asked for a code on every future login.");
      setSetupData(null); setCode(""); await refreshMe();
    } catch (e) {
      setErr(e.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const disableTotp = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password: pwd, code }, { withCredentials: true });
      setMsg("Two-Factor Authentication disabled.");
      setPwd(""); setCode(""); await refreshMe();
    } catch (e) {
      setErr(e.response?.data?.detail || "Disable failed");
    } finally { setLoading(false); }
  };

  const forceLogoutAll = async () => {
    if (!window.confirm("This will sign you out of every device immediately. Continue?")) return;
    try {
      await axios.post(`${API}/auth/force-logout-all`, {}, { withCredentials: true });
    } catch (err) {
      // Even if the server-side revoke fails (network error, expired token,
      // etc.) we still want to clear local auth state below — log for dev only.
      if (process.env.NODE_ENV === "development") console.warn("force-logout-all server call failed", err);
    }
    await logout();
    window.location.href = "/admin/login";
  };

  return (
    <div className="space-y-8" data-testid="admin-security-page">
      <header>
        <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold mb-1">Account Security</p>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white">Security & 2FA</h1>
        <p className="text-[#A1B2A8] text-sm mt-1">
          Signed in as <span className="text-white font-semibold">{user?.email}</span>
          <span className="ml-2 text-[#A1B2A8]/60">· role: {user?.role || "—"}</span>
        </p>
      </header>

      {(msg || err) && (
        <div className={`p-3 text-sm border ${err ? "border-red-500/30 bg-red-500/10 text-red-400" : "border-[#22C55E]/30 bg-[#22C55E]/5 text-[#22C55E]"}`}
             data-testid={err ? "security-error" : "security-success"}>
          {err || msg}
        </div>
      )}

      {/* 2FA card */}
      <section className="bg-[#09120D] border border-[#1A2E22] p-6">
        <div className="flex items-start gap-3 mb-5">
          {totpEnabled
            ? <ShieldCheck className="w-6 h-6 text-[#22C55E] mt-0.5" />
            : <ShieldOff className="w-6 h-6 text-amber-400 mt-0.5" />}
          <div>
            <h2 className="font-['Outfit'] text-lg font-semibold text-white">Two-Factor Authentication (TOTP)</h2>
            <p className="text-[#A1B2A8] text-sm">
              {totpEnabled
                ? "Active — your login is protected by a 6-digit code from your authenticator app."
                : "Recommended before going live. Works with Google Authenticator, Authy, 1Password, Microsoft Authenticator."}
            </p>
          </div>
        </div>

        {!totpEnabled && !setupData && (
          <button onClick={startSetup} disabled={loading}
            className="bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
            data-testid="totp-start-setup-btn">
            {loading ? "Generating…" : "Start setup"}
          </button>
        )}

        {!totpEnabled && setupData && (
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <p className="text-[#A1B2A8] text-xs uppercase tracking-wider mb-3">Step 1 — Scan with your authenticator app</p>
              <QrFromUri uri={setupData.provisioning_uri} />
              <p className="text-[#A1B2A8]/60 text-xs mt-3">
                Or enter this secret manually:
                <code className="block mt-1 text-[#22C55E] font-mono break-all" data-testid="totp-secret">
                  {setupData.secret}
                </code>
              </p>
            </div>
            <form onSubmit={enableTotp} className="space-y-3" data-testid="totp-enable-form">
              <p className="text-[#A1B2A8] text-xs uppercase tracking-wider">Step 2 — Enter the 6-digit code</p>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" autoFocus
                className="w-full bg-[#050A07] border border-[#1A2E22] text-white tracking-[0.5em] text-center text-lg py-3 focus:border-[#22C55E] outline-none"
                data-testid="totp-enable-code-input"
              />
              <button type="submit" disabled={loading || code.length !== 6}
                className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                data-testid="totp-enable-confirm-btn">
                {loading ? "Verifying…" : "Verify & Enable"}
              </button>
              <button type="button" onClick={() => { setSetupData(null); setCode(""); setErr(""); setMsg(""); }}
                className="w-full text-[#A1B2A8] hover:text-white text-xs"
                data-testid="totp-enable-cancel-btn">
                Cancel
              </button>
            </form>
          </div>
        )}

        {totpEnabled && (
          <form onSubmit={disableTotp} className="grid md:grid-cols-3 gap-3 max-w-2xl" data-testid="totp-disable-form">
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required
              placeholder="Account password"
              className="bg-[#050A07] border border-[#1A2E22] text-white py-2.5 px-3 text-sm focus:border-[#22C55E] outline-none"
              data-testid="totp-disable-password-input" />
            <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code" required
              className="bg-[#050A07] border border-[#1A2E22] text-white py-2.5 px-3 text-sm focus:border-[#22C55E] outline-none tracking-[0.3em] text-center"
              data-testid="totp-disable-code-input" />
            <button type="submit" disabled={loading || code.length !== 6 || !pwd}
              className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-400 px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
              data-testid="totp-disable-btn">
              {loading ? "Disabling…" : "Disable 2FA"}
            </button>
          </form>
        )}
      </section>

      {/* Force logout card */}
      <section className="bg-[#09120D] border border-[#1A2E22] p-6">
        <div className="flex items-start gap-3 mb-4">
          <RefreshCw className="w-6 h-6 text-[#22C55E] mt-0.5" />
          <div>
            <h2 className="font-['Outfit'] text-lg font-semibold text-white">Force Sign-Out — All Devices</h2>
            <p className="text-[#A1B2A8] text-sm">
              Rotates your session token version. Every existing access token issued for this account becomes
              invalid the moment you click — including any leaked or remembered session.
            </p>
          </div>
        </div>
        <button onClick={forceLogoutAll}
          className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-400 px-5 py-2.5 text-sm font-semibold"
          data-testid="force-logout-all-btn">
          Sign out everywhere
        </button>
      </section>

      <section className="bg-[#09120D] border border-[#1A2E22] p-6">
        <div className="flex items-start gap-3 mb-2">
          <KeyRound className="w-6 h-6 text-[#A1B2A8] mt-0.5" />
          <div>
            <h2 className="font-['Outfit'] text-lg font-semibold text-white">Best Practice</h2>
          </div>
        </div>
        <ul className="text-[#A1B2A8] text-sm space-y-1.5 list-disc pl-6 marker:text-[#22C55E]/60">
          <li>Founder Admin should always have 2FA enabled before go-live.</li>
          <li>Operations Admin handles approvals only — they cannot generate investor links or rotate keys.</li>
          <li>Never share master credentials. Each admin must have their own account + 2FA.</li>
          <li>If you ever suspect a leak, click <strong className="text-white">Sign out everywhere</strong> and rotate your password from the server <code>.env</code>.</li>
        </ul>
        {!totpEnabled && (
          <div className="mt-4 flex items-start gap-2 text-amber-400 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>You haven't enabled 2FA yet. We strongly recommend enabling it before opening the platform to investors.</p>
          </div>
        )}
        {totpEnabled && (
          <div className="mt-4 flex items-start gap-2 text-[#22C55E] text-sm">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>Two-factor authentication is active on your account.</p>
          </div>
        )}
      </section>
    </div>
  );
}
